'use client';

import { getAwaitingRegistrationSuccessRedirect, ROUTES, useClientConfig } from '@shared/utils';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from 'react';
import { getPlayRouteFallbackPath, shouldHandlePendingAuthReturn, syncPendingAuthReturnState } from '../../helpers/authPendingCallback';
import { isLoggedInRequiredRoute } from '../../helpers/isLoggedInRequiredRoute';
import { useSignIn } from '../../hooks/useSignIn';
import { useSession } from '../SessionProvider';
import { resolveAuthGate } from './authGuardLogic';
import { isRegistrationAuthComplete } from './registrationAuthSession';

const EMPTY_SEARCH_PARAMS = new URLSearchParams();

function AuthGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const signIn = useSignIn();
  const pathname = usePathname();
  const { session } = useSession();
  const config = useClientConfig();

  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(null);
  const isRedirectingToLobbyRef = useRef(false);
  const hasInitiatedSignInRef = useRef(false);

  const readSearchParams = useEffectEvent(() => {
    setSearchParams(new URLSearchParams(window.location.search));
  });

  useEffect(() => {
    readSearchParams();
  }, [pathname]);

  const buildCallbackUrl = useEffectEvent(() => {
    if (searchParams === null) {
      return null;
    }

    const qs = searchParams.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  });

  const handlePendingAuthReturn = useEffectEvent((lobbyPathname: string = pathname, isPageshowReturn = false) => {
    if (isRedirectingToLobbyRef.current) {
      return true;
    }

    const handlePlayRouteReturn = shouldHandlePendingAuthReturn(lobbyPathname, {
      hasInitiatedSignIn: hasInitiatedSignInRef.current,
      isPageshowReturn,
    });
    const redirected = syncPendingAuthReturnState(session.status, lobbyPathname, { handlePlayRouteReturn });

    if (redirected) {
      isRedirectingToLobbyRef.current = true;
    }

    return redirected;
  });

  const outcome = resolveAuthGate({
    pathname,
    searchParams: searchParams ?? EMPTY_SEARCH_PARAMS,
    session,
    registerRedirectUrl: config.legacySiteUrl,
    isLoggedInRequired: isLoggedInRequiredRoute(pathname),
    isRegistrationAuthComplete: isRegistrationAuthComplete(),
    isAwaitingRegistrationSuccessRedirect: getAwaitingRegistrationSuccessRedirect(),
  });

  useLayoutEffect(() => {
    const isPlayRoute = getPlayRouteFallbackPath(pathname) !== null;

    if (!isPlayRoute) {
      isRedirectingToLobbyRef.current = false;
      hasInitiatedSignInRef.current = false;
    }

    handlePendingAuthReturn(pathname);
  }, [pathname, session.status]);

  useEffect(() => {
    const onHistoryReturn = () => {
      handlePendingAuthReturn(window.location.pathname ?? '', true);
    };

    window.addEventListener('pageshow', onHistoryReturn);
    window.addEventListener('popstate', onHistoryReturn);
    return () => {
      window.removeEventListener('pageshow', onHistoryReturn);
      window.removeEventListener('popstate', onHistoryReturn);
    };
  }, []);

  useEffect(() => {
    if (searchParams === null) {
      return;
    }
    if (outcome.type === 'signIn') {
      if (handlePendingAuthReturn()) {
        return;
      }

      if (hasInitiatedSignInRef.current) {
        return;
      }

      const callbackUrl = buildCallbackUrl();
      if (callbackUrl !== null) {
        hasInitiatedSignInRef.current = true;
        signIn({ callbackUrl });
      }
      return;
    }
    if (outcome.type === 'profileRedirect') {
      router.push(ROUTES.MY_ACCOUNT.PROFILE);
    }
    if (outcome.type === 'selfExcludedRedirect') {
      const qs = searchParams.toString();
      router.push(qs ? `${ROUTES.TIME_OFF}?${qs}` : ROUTES.TIME_OFF);
    }
    if (outcome.type === 'fraudExcludedRedirect') {
      const qs = searchParams.toString();
      router.push(qs ? `${ROUTES.HOME}?${qs}` : ROUTES.HOME);
    }
  }, [outcome.type, pathname, router, searchParams, signIn]);

  if (outcome.type === 'allow') {
    return <>{children}</>;
  }

  if (
    outcome.type === 'wait' ||
    outcome.type === 'profileRedirect' ||
    outcome.type === 'signIn' ||
    outcome.type === 'selfExcludedRedirect' ||
    outcome.type === 'fraudExcludedRedirect'
  ) {
    return null;
  }

  // Exhaustiveness guard: TypeScript narrows outcome to `never` here only when
  // every member of AuthGateOutcome has been handled above. If a new type is
  // added without a handler, the assignment below becomes a compile-time error.
  const exhaustiveCheck: never = outcome;
  throw new Error(`Unhandled auth gate outcome: ${JSON.stringify(exhaustiveCheck)}`);
}

export { AuthGuard };
